import { useEffect, useState } from "react";
import "@/App.css";
import "@/index.css";
import { Toaster } from "sonner";
import { useT } from "@/i18n";
import client, { authApi } from "@/lib/api";
import PublicHome from "@/pages/PublicHome";
import LoginPage from "@/pages/LoginPage";
import AdminDashboard from "@/pages/AdminDashboard";

function App() {
  const [lang, setLang] = useState(() => localStorage.getItem("amatour_lang") || "fr");
  const t = useT(lang);
  const [view, setView] = useState("home"); // home | login | admin | paiement
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [content, setContent] = useState(null);

  useEffect(() => { localStorage.setItem("amatour_lang", lang); }, [lang]);

  useEffect(() => {
    client.get("/content").then((r) => setContent(r.data)).catch(() => {});
    const token = localStorage.getItem("amatour_token");
    if (!token) { setChecking(false); return; }
    authApi.me().then((r) => setUser(r.data)).catch(() => localStorage.removeItem("amatour_token")).finally(() => setChecking(false));

    // Payment return: /?session_id=...
    const params = new URLSearchParams(window.location.search);
    if (params.get("session_id")) setView("paiement");
    if (window.location.pathname === "/paiement") setView("paiement");
  }, []);

  const goLogin = () => setView("login");
  const goHome = () => { setView("home"); window.history.replaceState({}, "", "/"); };
  const onLogged = (u) => { setUser(u); setView("admin"); };
  const logout = () => { localStorage.removeItem("amatour_token"); setUser(null); setView("home"); };

  if (checking) return <div className="amatour-shell flex items-center justify-center min-h-screen"><i className="fa-solid fa-spinner spin text-3xl text-amber-300" /></div>;

  return (
    <>
      <Toaster theme="dark" position="top-right" />
      {view === "home" && <PublicHome t={t} lang={lang} setLang={setLang} onLogin={goLogin} content={content} />}
      {view === "login" && <LoginPage t={t} onSuccess={onLogged} onBack={goHome} />}
      {view === "admin" && user && <AdminDashboard t={t} user={user} onLogout={logout} />}
      {view === "paiement" && <PaymentReturn t={t} onBack={goHome} />}
    </>
  );
}

function PaymentReturn({ t, onBack }) {
  const [state, setState] = useState({ status: "checking" });
  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get("session_id");
    const cancelled = new URLSearchParams(window.location.search).get("annule");
    if (cancelled) { setState({ status: "cancelled" }); return; }
    if (!sid) { setState({ status: "unknown" }); return; }
    let tries = 0;
    const iv = setInterval(async () => {
      tries++;
      try {
        const { data } = await client.get(`/payment-status/${sid}`);
        if (data.payment_status === "paid") { clearInterval(iv); setState({ status: "paid", ...data }); }
        else if (tries > 15) { clearInterval(iv); setState({ status: "pending", ...data }); }
      } catch {
        clearInterval(iv); setState({ status: "error" });
      }
    }, 2000);
    return () => clearInterval(iv);
  }, []);
  return (
    <div className="amatour-shell grain min-h-screen flex items-center justify-center px-6">
      <div className="card-amatour p-12 max-w-lg w-full text-center fade-in">
        {state.status === "checking" && <><i className="fa-solid fa-spinner spin text-4xl text-amber-300 mb-4" /><h2 className="font-display text-2xl">Vérification du paiement…</h2></>}
        {state.status === "paid" && <><i className="fa-solid fa-circle-check text-5xl text-emerald-400 mb-4" /><h2 className="font-display text-3xl mb-2 crown-accent">Paiement confirmé !</h2><p className="text-zinc-400">Merci pour votre soutien à Amatour Festival. Vous recevrez un email de confirmation à {t.contact.email}.</p></>}
        {state.status === "cancelled" && <><i className="fa-solid fa-circle-xmark text-5xl text-red-400 mb-4" /><h2 className="font-display text-2xl">Paiement annulé</h2></>}
        {state.status === "pending" && <><i className="fa-solid fa-hourglass text-4xl text-amber-300 mb-4" /><h2 className="font-display text-2xl mb-2">Paiement en cours de traitement</h2><p className="text-zinc-400">Cela peut prendre quelques minutes.</p></>}
        {(state.status === "error" || state.status === "unknown") && <><i className="fa-solid fa-triangle-exclamation text-4xl text-red-400 mb-4" /><h2 className="font-display text-2xl">Erreur</h2></>}
        <button className="btn-gold mt-6" onClick={onBack} data-testid="payment-back">Retour au site</button>
      </div>
    </div>
  );
}

export default App;
